// Compact, XSS-safe Markdown -> HTML renderer for chat replies, with KaTeX math.
//
// Why this exists: the SLM streams markdown (headings, **bold**, lists, `code`,
// fenced ``` blocks). The chat UI previously printed that raw, so formatting
// only "looked right" by accident -- and differed between a freshly-streamed
// message and one reloaded from the DB. Both the streaming path and the reload
// path now render through THIS one function, so the output is identical in
// either state and the user never has to refresh to get the format right.
//
// The output is sized for the legacy `.msg__body` CSS (see styles/legacy.css):
// headings h1-h4, ul/ol/li, blockquote, strong/em, inline <code>, and
// <pre class="code"><code> for fenced blocks.

import katex from 'katex';

const ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
};

// Private-use sentinel wrapping a code-span index. Cannot occur in real model
// output, so restoring spans below never collides with literal page text.
const SENTINEL = '';

/** Neutralise HTML so model output can never inject markup/script. Runs before
 *  any markdown transform, so every `<`/`>`/`&` the model emits is inert. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ESCAPE_MAP[c] ?? c);
}

/** Render one LaTeX span to KaTeX HTML. `throwOnError:false` makes KaTeX emit a
 *  red inline error node instead of throwing on malformed TeX; the try/catch is
 *  a final backstop that falls back to the raw (escaped) source so a bad formula
 *  degrades to readable text rather than breaking the whole reply. `trust:false`
 *  (default) blocks \href/\includegraphics, so rendered math can't inject links. */
function renderMath(tex: string, display: boolean): string {
  try {
    return katex.renderToString(tex.trim(), {
      displayMode: display,
      throwOnError: false,
      output: 'html',
    });
  } catch {
    return escapeHtml(tex.trim());
  }
}

// Private-use sentinels wrapping a stash index. U+F8FF is already taken by the
// inline code-span machinery, so math/code use their own codepoints — none can
// occur in real model output, so restoring them never collides with page text.
const MATH_SENTINEL = '';
const CODE_SENTINEL = '';

/**
 * Pull LaTeX math out of the source BEFORE markdown parsing and replace each
 * span with a sentinel token; the caller substitutes the rendered KaTeX back
 * into the final HTML. Extraction must precede escapeHtml so backslashes and
 * braces survive, and precede the block parser so `\[…\]` on its own lines
 * isn't mangled.
 *
 * Delimiters: `\[ … \]` and `$$ … $$` render as display (block) math; `\( … \)`
 * as inline. Bare single-`$` is intentionally NOT treated as math — it collides
 * with currency ("$5") far too often to be safe.
 *
 * Code is protected first (fenced blocks, then inline spans) so a literal
 * `\(x\)` shown inside code stays literal instead of rendering as math.
 */
function extractMath(src: string): { text: string; math: string[] } {
  const math: string[] = [];
  const stash = (html: string, block: boolean): string => {
    const token = `${MATH_SENTINEL}${math.length}${MATH_SENTINEL}`;
    math.push(html);
    // Block math on its own line → a standalone paragraph in the output.
    return block ? `\n\n${token}\n\n` : token;
  };

  const code: string[] = [];
  const hideCode = (m: string): string => {
    const token = `${CODE_SENTINEL}${code.length}${CODE_SENTINEL}`;
    code.push(m);
    return token;
  };
  // Fenced code first (matched closer must equal the opener), then inline spans.
  let s = src.replace(/(^|\n)(`{2,}|~{2,})[^\n]*\n[\s\S]*?\n[ \t]*\2[ \t]*(?=\n|$)/g, hideCode);
  s = s.replace(/`[^`\n]+`/g, hideCode);

  s = s.replace(/\\\[([\s\S]+?)\\\]/g, (_m, tex: string) => stash(renderMath(tex, true), true));
  s = s.replace(/\$\$([\s\S]+?)\$\$/g, (_m, tex: string) => stash(renderMath(tex, true), true));
  s = s.replace(/\\\(([\s\S]+?)\\\)/g, (_m, tex: string) => stash(renderMath(tex, false), false));

  // Single-$ inline math — accepted ONLY when the span carries an unmistakable
  // LaTeX signal: a \command, or ^/_ scripting. This is why bare `$` was rejected
  // outright before ("$5", "$1,200"), and the cost was severe: models write
  // `$a = \frac{dv}{dt}$` by default, so a whole physics answer rendered as raw
  // LaTeX source on screen. The guard is STRUCTURAL, not a vocabulary list —
  // currency cannot contain a backslash command or a superscript, so it stays
  // literal, while real math is recognised whichever delimiter the model reached
  // for. Bounded length so an unclosed `$` cannot swallow a paragraph.
  s = s.replace(/\$([^$\n]{1,300})\$/g, (m: string, tex: string) =>
    /\\[a-zA-Z]+|[\^_]\s*\{|[\^_][A-Za-z0-9]/.test(tex)
      ? stash(renderMath(tex, false), false)
      : m);

  if (code.length) {
    s = s.replace(new RegExp(`${CODE_SENTINEL}(\\d+)${CODE_SENTINEL}`, 'g'), (_m, n: string) => code[Number(n)] ?? '');
  }
  return { text: s, math };
}

// Two-page copy glyph for the compact-list Copy button, matching the icon on
// the prompt/reply copy controls (Messages.tsx CopyGlyph). Emitted as raw SVG
// markup because the button lives in dangerouslySetInnerHTML; the click handler
// swaps it for a check on success and back again after ~1.6s.
const COPY_ICON =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>' +
  '<path d="M5 15V5a2 2 0 0 1 2-2h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

/** Inline-level markdown on already-escaped text: code spans, links, bold,
 *  italic. Code spans are pulled out first so `**` inside them stays literal. */
function renderInline(text: string): string {
  const codeSpans: string[] = [];
  let out = text.replace(/`([^`]+)`/g, (_m, code: string) => {
    codeSpans.push(`<code>${code}</code>`);
    return `${SENTINEL}${codeSpans.length - 1}${SENTINEL}`;
  });

  // ![alt](url) images — MUST run before the link rule below (which would
  // otherwise match the [alt](url) portion and leave a stray "!"). Only inline
  // base64 data:image URLs (generated images) or http(s) served URLs are allowed
  // (no javascript:/other schemes). Styling (size cap, border, zoom cursor) and
  // click-to-open-full live on the `.chat-img` class in legacy.css / Messages.tsx.
  out = out.replace(
    /!\[([^\]]*)\]\((data:image\/[a-zA-Z0-9.+-]+;base64,[^\s)]+|https?:\/\/[^\s)]+|\/[^\s)]+)\)/g,
    (_m, alt: string, url: string) =>
      `<img src="${url}" alt="${alt || 'Generated image'}" loading="lazy" class="chat-img" />`,
  );

  // [label](http(s)://url) -- only http(s), to avoid javascript: URLs.
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, label: string, url: string) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`,
  );

  // Bare-URL autolink: turn a naked https?://… into a link. Stash the anchors/
  // images already built above (distinct `A` sentinel so it can't collide with the
  // numeric code-span sentinel) so an href/src is never double-wrapped, linkify the
  // remaining naked URLs, then restore.
  const anchors: string[] = [];
  out = out.replace(/<(?:a|img)\b[^>]*>(?:[^<]*<\/a>)?/g, (m) => {
    anchors.push(m);
    return `${SENTINEL}A${anchors.length - 1}${SENTINEL}`;
  });
  out = out.replace(
    /(^|[\s(])(https?:\/\/[^\s<)]+)/g,
    (_m, pre: string, url: string) => `${pre}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
  );
  out = out.replace(new RegExp(`${SENTINEL}A(\\d+)${SENTINEL}`, 'g'), (_m, n: string) => anchors[Number(n)] ?? '');

  // ~~strikethrough~~ (code spans are already stashed, so tildes in code stay literal).
  out = out.replace(/~~([^~]+)~~/g, '<del>$1</del>');

  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  out = out.replace(/(^|[^_\w])_([^_\n]+)_/g, '$1<em>$2</em>');

  out = out.replace(
    new RegExp(`${SENTINEL}(\\d+)${SENTINEL}`, 'g'),
    (_m, i: string) => codeSpans[Number(i)] ?? '',
  );
  return out;
}

// ── Callouts ────────────────────────────────────────────────────────────────
// A warning must LOOK like a warning, and an example must not look like an
// explanation. Rather than sniffing prose for words like "note:" (a guess that
// misfires), we implement the ESTABLISHED alert syntax the models already know:
// GitHub's `> [!NOTE]` set, plus the Obsidian additions we need (example,
// success, info). Deterministic, spec-backed, and it degrades to an ordinary
// blockquote when the tag is absent.
//
//   > [!WARNING]
//   > Don't run this against production.
//
// An optional title may follow the tag (`> [!NOTE] Read this first`).
const CALLOUT_ICONS: Record<string, string> = {
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 7.6v.5"/>',
  tip: '<path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.5.4.8 1 .9 1.6h5.4c.1-.6.4-1.2.9-1.6A6 6 0 0 0 12 3Z"/>',
  important: '<path d="M12 3.5 21 19H3l9-15.5Z"/><path d="M12 10v4M12 16.6v.4"/>',
  warning: '<path d="M12 3.5 21 19H3l9-15.5Z"/><path d="M12 10v4M12 16.6v.4"/>',
  caution: '<circle cx="12" cy="12" r="9"/><path d="m9 9 6 6M15 9l-6 6"/>',
  success: '<circle cx="12" cy="12" r="9"/><path d="m8.2 12.2 2.6 2.6 5-5.4"/>',
  example: '<path d="M5 4.5h9l5 5V19a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19V6a1.5 1.5 0 0 1 1.5-1.5Z"/><path d="M14 4.5v5h5"/>',
};
/** Tag → (css modifier, default label). `note` shares the info glyph. */
const CALLOUT_KINDS: Record<string, { icon: string; label: string }> = {
  note: { icon: 'info', label: 'Note' },
  info: { icon: 'info', label: 'Note' },
  tip: { icon: 'tip', label: 'Tip' },
  important: { icon: 'important', label: 'Important' },
  warning: { icon: 'warning', label: 'Warning' },
  caution: { icon: 'caution', label: 'Caution' },
  success: { icon: 'success', label: 'Done' },
  example: { icon: 'example', label: 'Example' },
};

/** 20px stroke icon, matching the app's other inline glyphs (1.7 stroke,
 *  currentColor) so a callout reads as product chrome, not clip-art. */
function calloutIcon(kind: string): string {
  const d = CALLOUT_ICONS[kind] ?? CALLOUT_ICONS.info ?? '';
  return (
    '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + d + '</svg>'
  );
}

/** A GFM table separator row, e.g. `|---|:--:|--:|` or `--- | ---`. Must contain
 *  a pipe and at least one dash, and only table-separator characters. */
function isTableSeparator(line: string): boolean {
  const s = line.trim();
  return s.includes('|') && s.includes('-') && /^[\s:|-]+$/.test(s);
}

/** Split one `| a | b | c |` row into trimmed cells, dropping the leading/
 *  trailing empty cells produced by the outer pipes. */
function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

/** Column alignment from the separator row (`:--`, `:--:`, `--:`). */
function columnAlign(sep: string): '' | 'left' | 'right' | 'center' {
  const l = sep.startsWith(':');
  const r = sep.endsWith(':');
  return l && r ? 'center' : r ? 'right' : l ? 'left' : '';
}

/**
 * Convert a raw HTML `<table>…</table>` block into an equivalent GFM pipe table
 * so the SAFE renderer below draws it. Models sometimes emit literal
 * `<table><tr><td>…` (a real failure: the multiplication-table reply printed the
 * tags verbatim because escapeHtml turns them into visible text). This is a
 * TEXT-ONLY extraction — we pull each cell's text, re-feed it as markdown, and
 * the existing GFM path re-escapes it — so the model's raw HTML is NEVER injected
 * and the XSS boundary is unchanged. Tables inside code fences are left literal
 * (a code sample about HTML must stay as written).
 */
function htmlTablesToPipes(src: string): string {
  if (!/<table[\s>]/i.test(src)) return src; // fast path — nothing to convert
  const S = '@@CF@@'; // fence sentinel (plain ASCII, never in model text)
  // Protect fenced code so a <table> shown as an example is untouched.
  const code: string[] = [];
  let s = src.replace(/(^|\n)(`{2,}|~{2,})[^\n]*\n[\s\S]*?\n[ \t]*\2[ \t]*(?=\n|$)/g, (m) => {
    code.push(m);
    return `${S}C${code.length - 1}${S}`;
  });
  const cellText = (cell: string): string =>
    cell
      .replace(/<br\s*\/?>/gi, ' ') // line break inside a cell → space
      .replace(/<[^>]+>/g, '') // strip any inner tags (bold/links/etc. → text)
      .replace(/&nbsp;/gi, ' ').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"').replace(/&#39;/gi, "'").replace(/&amp;/gi, '&')
      .replace(/\s+/g, ' ').trim()
      // A literal pipe in a cell would split the row (this renderer's splitTableRow
      // doesn't honor GFM "\|" escapes), so swap it for a look-alike fullwidth bar.
      .replace(/\|/g, '｜');
  s = s.replace(/<table\b[\s\S]*?<\/table>/gi, (block) => {
    const rows = [...block.matchAll(/<tr\b[^>]*>([\s\S]*?)<\/tr>/gi)]
      .map((m) => [...(m[1] ?? '').matchAll(/<(th|td)\b[^>]*>([\s\S]*?)<\/(?:th|td)>/gi)].map((c) => cellText(c[2] ?? '')))
      .filter((r) => r.length > 0);
    if (rows.length === 0) return block; // unparseable → leave it as-is
    const width = Math.max(...rows.map((r) => r.length));
    const pad = (r: string[]): string[] => (r.length < width ? [...r, ...Array(width - r.length).fill('')] : r);
    const line = (r: string[]): string => `| ${pad(r).join(' | ')} |`;
    const sep = `| ${Array(width).fill('---').join(' | ')} |`;
    const body = rows.slice(1).map(line);
    // A single-row HTML table isn't a GFM table (needs a header+separator); still
    // give it a separator so it renders rather than collapsing to one pipe line.
    return `\n\n${[line(rows[0] ?? []), sep, ...body].join('\n')}\n\n`;
  });
  if (code.length) s = s.replace(new RegExp(`${S}C(\\d+)${S}`, 'g'), (_m, n: string) => code[Number(n)] ?? '');
  return s;
}

/** One parsed list item. `indent` is the leading-whitespace column count (tabs =
 *  2 cols); `task` is null for a normal bullet, or true/false for `- [x]`/`- [ ]`. */
interface ListItem { indent: number; ordered: boolean; text: string; task: boolean | null; }

/** Parse a single line as a list item (bullet `-*+` or ordered `1.`/`1)`), or
 *  null. Indentation-aware so the block renderer can nest sub-items. */
function parseListItem(line: string): ListItem | null {
  const m = line.match(/^(\s*)([-*+]|\d+[.)])\s+(.*)$/);
  if (!m) return null;
  const indent = (m[1] ?? '').replace(/\t/g, '  ').length; // a tab counts as 2 columns
  const ordered = /\d/.test(m[2] ?? '');
  let text = m[3] ?? '';
  let task: boolean | null = null;
  const t = text.match(/^\[([ xX])\]\s+(.*)$/);
  if (!ordered && t) { task = /[xX]/.test(t[1] ?? ''); text = t[2] ?? ''; }
  return { indent, ordered, text, task };
}
// Code-fence detection. The SLM does NOT reliably emit clean ```lang fences --
// observed output includes two backticks with a space before the language
// (`` bash), bare `` closers, indented fences, and tildes. A line that is
// ONLY fence characters (2+ backticks or tildes) plus an optional language
// word is treated as a fence marker; anything with real content after the
// backticks (e.g. inline `code`, or a `word` on its own line) is left alone.
const FENCE_OPEN = /^\s*(?:`{2,}|~{2,})[ \t]*([A-Za-z0-9_.+#-]*)[ \t]*$/;
const FENCE_CLOSE = /^\s*(?:`{2,}|~{2,})[ \t]*$/;

/** Block-level markdown -> HTML. Streamed mid-message text (e.g. an unclosed
 *  fence) still renders sensibly; it just resolves as the rest arrives. */
export function renderMarkdown(src: string): string {
  if (!src) return '';
  // Strip internal retrieval markers the model ECHOED into its answer. <SOURCES>…
  // </SOURCES> is a CONTEXT block Brew injects for grounding (server seed/wrappers.ts) —
  // the model must CITE from it, never repeat it. When it leaks (especially on turns
  // with no structured `sources` to trigger the normal stripper), the escaped tag
  // shows as a literal "<SOURCES>". Remove the whole block, a bare unclosed one (the
  // model often dumps it at the very end), and any stray tag — always, on every path.
  src = src
    .replace(/<SOURCES>[\s\S]*?<\/SOURCES>/gi, '')
    .replace(/<SOURCES>[\s\S]*$/i, '')
    .replace(/<\/?SOURCES>/gi, '')
    .trimEnd();
  // Pull LaTeX out to sentinel tokens up front (before escaping/parsing); the
  // rendered KaTeX is spliced back into the final HTML at the end.
  const { text: withMath, math } = extractMath(htmlTablesToPipes(src.replace(/\r\n/g, '\n')));
  const lines = withMath.split('\n');
  const html: string[] = [];
  let i = 0;
  let listType: 'ul' | 'ol' | null = null;
  // List items are buffered (not pushed straight to `html`) so that when the
  // list closes we can pick its layout from the WHOLE set: a run of short,
  // single-line items (an enumeration — alphabets, option lists, glossaries)
  // renders as a compact multi-column grid instead of a tall one-per-row
  // column. This is what makes e.g. the Hindi वर्णमाला read as a tidy grid
  // rather than 46 stacked rows.
  let listItems: string[] = [];

  /** Visible length of a rendered <li>'s inner HTML, ignoring tags/entities —
   *  used to decide whether the item is "short" enough for a grid cell. */
  const visibleLen = (s: string): number =>
    s.replace(/<[^>]+>/g, '').replace(/&[a-z]+;/gi, ' ').trim().length;

  const closeList = (): void => {
    if (!listType) return;
    // Compact grid only when there are enough items to benefit AND every item
    // is a short single-liner with no block content (image/code block). Prose
    // lists (long sentences, nested bullets) keep the normal stacked layout.
    const compact =
      listItems.length >= 6 &&
      listItems.every((it) => visibleLen(it) <= 28 && !/<(img|pre|ul|ol)\b/i.test(it));
    if (compact) {
      // Wrap the grid in a .md-list-block so a hover-revealed Copy button can
      // sit in its corner — same affordance as fenced code (see Messages.tsx
      // delegated handler, which reads the list's <li> text at click time).
      html.push(
        '<div class="md-list-block">' +
          `<button class="list-copy" type="button" aria-label="Copy list" title="Copy">${COPY_ICON}</button>` +
          `<${listType} class="md-list--compact">${listItems.join('')}</${listType}>` +
          '</div>',
      );
    } else {
      html.push(`<${listType}>${listItems.join('')}</${listType}>`);
    }
    listType = null;
    listItems = [];
  };

  /** Render a whole contiguous list block with real nesting + task items.
   *  Raw indents are normalised to consecutive depths (0,1,2…), clamping any
   *  indent JUMP to +1 so we never emit an invalid `<ul><ul>` with no `<li>`
   *  between. A single-level, all-short, non-task enumeration keeps the compact
   *  grid layout (the Hindi-alphabet case); everything else nests normally. */
  const renderListBlock = (items: ListItem[]): string => {
    const indentStack: number[] = [];
    const depth = items.map((it) => {
      while (indentStack.length && it.indent < (indentStack[indentStack.length - 1] ?? 0)) indentStack.pop();
      if (!indentStack.length || it.indent > (indentStack[indentStack.length - 1] ?? 0)) indentStack.push(it.indent);
      return indentStack.length - 1;
    });
    const inner = (it: ListItem): string =>
      it.task === null
        ? renderInline(escapeHtml(it.text))
        : `<input type="checkbox" disabled${it.task ? ' checked' : ''}/> ${renderInline(escapeHtml(it.text))}`;

    // Compact grid: a single-level, all-short, non-task enumeration only.
    if (
      Math.max(...depth) === 0 &&
      items.length >= 6 &&
      items.every((it) => it.task === null && visibleLen(inner(it)) <= 28 && !/<(img|pre|ul|ol)\b/i.test(inner(it)))
    ) {
      const type = items[0]?.ordered ? 'ol' : 'ul';
      const lis = items.map((it) => `<li>${inner(it)}</li>`).join('');
      return (
        '<div class="md-list-block">' +
        `<button class="list-copy" type="button" aria-label="Copy list" title="Copy">${COPY_ICON}</button>` +
        `<${type} class="md-list--compact">${lis}</${type}>` +
        '</div>'
      );
    }

    // General case: walk items with a stack of open list types, one per depth.
    const out: string[] = [];
    const stack: Array<'ul' | 'ol'> = [];
    items.forEach((it, k) => {
      const d = depth[k] ?? 0;
      const type: 'ul' | 'ol' = it.ordered ? 'ol' : 'ul';
      if (d + 1 > stack.length) {
        out.push(`<${type}>`);              // deeper (or first) → open a nested list inside the open <li>
        stack.push(type);
      } else {
        while (stack.length > d + 1) out.push(`</li></${stack.pop()}>`); // close deeper lists
        out.push('</li>');                  // close the sibling <li> at this depth
        if (stack[d] !== type) {            // same depth, different marker → switch list
          out.push(`</${stack.pop()}><${type}>`);
          stack.push(type);
        }
      }
      out.push(`<li${it.task === null ? '' : ' class="task-item"'}>${inner(it)}`);
    });
    while (stack.length) out.push(`</li></${stack.pop()}>`);
    return out.join('');
  };

  while (i < lines.length) {
    const line = lines[i] ?? '';

    // Fenced code block -- tolerant of the SLM's malformed fences (see FENCE_*).
    const fence = line.match(FENCE_OPEN);
    if (fence) {
      closeList();
      const lang = (fence[1] ?? '').toLowerCase();
      const body: string[] = [];
      i++;
      while (i < lines.length && !FENCE_CLOSE.test(lines[i] ?? '')) {
        body.push(lines[i] ?? '');
        i++;
      }
      i++; // consume the closing fence (or fall off the end while streaming)
      // Wrapped in .code-block so the language label + copy button can sit in
      // the corner. The button carries no data -- the click handler
      // (Messages.tsx) reads the <code> textContent at copy time, so there's no
      // second copy of the text to escape or keep in sync while streaming.
      // A mermaid fence is a DIAGRAM, not code. Emit a container carrying the
      // source; Messages.tsx lazy-loads mermaid and renders it into an SVG.
      //
      // Why bother: models write mermaid well and unprompted, but with no
      // renderer it lands as a wall of "graph TD; A-->B" text — so asking for a
      // flow chart got you source code to paste into some other tool. Rendering
      // it is the cheapest good diagram in the product: no file, no skill, no
      // image model, no round trip.
      //
      // The source is escaped into a data attribute rather than the element body
      // so a half-streamed fence cannot inject markup, and the <pre> fallback
      // still shows something readable if mermaid fails to load or parse.
      // Render as a mermaid DIAGRAM when the fence is tagged ```mermaid OR — since
      // models frequently emit correct mermaid but forget the language tag (which
      // otherwise lands as a wall of code, e.g. an agentic "flow chart" reply) — when
      // an untagged/generic-text fence's content STARTS WITH a mermaid diagram
      // keyword. The keyword guard keeps real code (js/py/…) out of the diagram path.
      const fenceBody = body.join('\n');
      const startsWithMermaidKeyword =
        /^\s*(?:%%\{[\s\S]*?\}%%\s*)?(?:flowchart|graph|sequenceDiagram|classDiagram|stateDiagram(?:-v2)?|erDiagram|journey|gantt|pie|mindmap|timeline|quadrantChart|xychart-beta|sankey-beta|gitGraph|C4Context|requirementDiagram|block-beta)\b/.test(fenceBody);
      const isMermaidFence =
        lang === 'mermaid' ||
        ((lang === '' || /^(?:text|txt|plain(?:text)?)$/i.test(lang)) && startsWithMermaidKeyword);
      if (isMermaidFence && body.length > 0) {
        const src = escapeHtml(fenceBody);
        html.push(
          `<div class="mermaid-block" data-mermaid="${src}">` +
            `<pre class="mermaid-src"><code>${src}</code></pre>` +
            '</div>',
        );
        continue;
      }
      // An ```svg fence is a FIGURE, not code — the second half of the visual
      // layer. Mermaid is a graph language with no geometry, so a shape (a
      // right-angled triangle, a circle with a radius, a plotted curve) has no
      // valid mermaid form; before this fence existed, asking for one got a
      // flowchart of the formula. Same contract as mermaid above: the source goes
      // into a data attribute (a half-streamed fence can never inject markup) and
      // Messages.tsx sanitizes it with DOMPurify before it reaches the DOM.
      //
      // Untagged fences are NOT sniffed for `<svg`, deliberately: a reply that
      // teaches SVG markup must keep showing the code. A figure is an explicit
      // ```svg fence, which is exactly what the prompt asks the model for.
      const isSvgFence = lang === 'svg' && /<svg[\s>]/i.test(fenceBody);
      if (isSvgFence && body.length > 0) {
        const src = escapeHtml(fenceBody);
        html.push(
          `<div class="svg-block" data-svg="${src}">` +
            `<pre class="svg-src"><code>${src}</code></pre>` +
            '</div>',
        );
        continue;
      }
      const langLabel = lang ? `<span class="code-lang">${escapeHtml(lang)}</span>` : '';
      html.push(
        '<div class="code-block">' +
          langLabel +
          '<button class="code-copy" type="button" aria-label="Copy code">Copy</button>' +
          `<pre class="code"><code>${escapeHtml(body.join('\n'))}</code></pre>` +
          '</div>',
      );
      continue;
    }

    // Heading # ... ######
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      closeList();
      const level = Math.min(6, (heading[1] ?? '#').length);
      html.push(`<h${level}>${renderInline(escapeHtml((heading[2] ?? '').trim()))}</h${level}>`);
      i++;
      continue;
    }

    // Blockquote > … — and its typed variant, the CALLOUT.
    //
    // Two fixes over the old one-liner: (1) the quote body is rendered as REAL
    // BLOCKS (recursing through this same parser) instead of joining every line
    // with a space, so a multi-line quote keeps its paragraphs and can hold a
    // list or a code block; (2) a leading alert tag promotes the quote to a
    // typed callout, so a warning is visually a warning rather than a quote.
    if (/^>\s?/.test(line)) {
      closeList();
      const quote: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i] ?? '')) {
        quote.push((lines[i] ?? '').replace(/^>\s?/, ''));
        i++;
      }
      const alert = (quote[0] ?? '').match(/^\s*\[!(\w+)\]\s*(.*)$/);
      const kind = alert ? (alert[1] ?? '').toLowerCase() : '';
      const spec = CALLOUT_KINDS[kind];
      if (spec) {
        // Title is optional: `> [!NOTE] Read this first` overrides the default label.
        const title = (alert?.[2] ?? '').trim();
        const bodyMd = quote.slice(1).join('\n').trim();
        html.push(
          `<div class="callout callout--${kind}" role="note">` +
            `<span class="callout__icon">${calloutIcon(spec.icon)}</span>` +
            '<div class="callout__main">' +
              `<span class="callout__label">${renderInline(escapeHtml(title || spec.label))}</span>` +
              (bodyMd ? `<div class="callout__body">${renderMarkdown(bodyMd)}</div>` : '') +
            '</div>' +
          '</div>',
        );
        continue;
      }
      html.push(`<blockquote>${renderMarkdown(quote.join('\n'))}</blockquote>`);
      continue;
    }

    // List block (unordered -/*/+, ordered 1./1), task -[ ]/-[x]) — collect the
    // WHOLE contiguous block (including indented sub-items and lazy continuations)
    // and render it with real nesting via renderListBlock().
    if (parseListItem(line)) {
      closeList();
      const items: ListItem[] = [];
      while (i < lines.length) {
        const l = lines[i] ?? '';
        const it = parseListItem(l);
        if (it) { items.push(it); i++; continue; }
        if (/^\s*$/.test(l)) {
          // blank line: keep the list open only if the NEXT line resumes it
          if (parseListItem(lines[i + 1] ?? '')) { i++; continue; }
          break;
        }
        if (/^\s+\S/.test(l) && items.length) {
          // indented non-item line → lazy continuation of the previous item
          const last = items[items.length - 1];
          if (last) last.text += ' ' + l.trim();
          i++;
          continue;
        }
        break;
      }
      html.push(renderListBlock(items));
      continue;
    }

    // Horizontal rule. Emitted always; the stylesheet decides whether it EARNS
    // its space — a rule that introduces a heading renders as a section break,
    // a decorative one between paragraphs stays hidden (see legacy.css).
    if (/^\s*(---|\*\*\*|___)\s*$/.test(line)) {
      closeList();
      html.push('<hr/>');
      i++;
      continue;
    }

    // Blank line -- paragraph/list separator
    if (/^\s*$/.test(line)) {
      closeList();
      i++;
      continue;
    }

    // GFM table -- a header row with pipes immediately followed by a separator
    // row, then zero+ body rows. Rendered as a real <table> inside a horizontal
    // scroll wrapper so wide tables (e.g. competitor comparisons) never overflow
    // the chat bubble. Without this, tables printed as raw `| a | b |` text.
    if (line.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1] ?? '')) {
      closeList();
      const header = splitTableRow(line);
      const aligns = splitTableRow(lines[i + 1] ?? '').map(columnAlign);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length) {
        const l = lines[i] ?? '';
        if (/^\s*$/.test(l) || !l.includes('|')) break;
        rows.push(splitTableRow(l));
        i++;
      }
      const alignAttr = (idx: number): string =>
        aligns[idx] ? ` style="text-align:${aligns[idx]}"` : '';
      const thead = header
        .map((c, idx) => `<th${alignAttr(idx)}>${renderInline(escapeHtml(c))}</th>`)
        .join('');
      const tbody = rows
        .map((r) => {
          const tds = header
            .map((_c, idx) => `<td${alignAttr(idx)}>${renderInline(escapeHtml(r[idx] ?? ''))}</td>`)
            .join('');
          return `<tr>${tds}</tr>`;
        })
        .join('');
      html.push(
        `<div class="md-table-wrap"><table class="md-table"><thead><tr>${thead}</tr></thead><tbody>${tbody}</tbody></table></div>`,
      );
      continue;
    }

    // Delimiter-less table fallback -- the SLM often omits the `|---|` separator
    // (e.g. transaction summaries), which the GFM block above requires. A run of
    // 2+ pipe rows with no separator renders here as a header-less table so it
    // never leaks as raw `| a | b |` text. Two consecutive rows are required so
    // a lone prose line with a stray `|` isn't mistaken for a table.
    const looksLikeRow = (l: string): boolean =>
      l.includes('|') && (l.trim().startsWith('|') || /\S\s*\|\s*\S/.test(l));
    if (looksLikeRow(line) && looksLikeRow(lines[i + 1] ?? '')) {
      closeList();
      const rows: string[][] = [];
      while (i < lines.length && looksLikeRow(lines[i] ?? '')) {
        rows.push(splitTableRow(lines[i] ?? ''));
        i++;
      }
      // Treat the FIRST collected row as the header so the header-shading + zebra
      // CSS applies (matches the GFM table above), instead of a header-less body.
      const [head, ...body] = rows;
      const thead = `<thead><tr>${(head ?? []).map((c) => `<th>${renderInline(escapeHtml(c))}</th>`).join('')}</tr></thead>`;
      const tbody = body
        .map((r) => `<tr>${r.map((c) => `<td>${renderInline(escapeHtml(c))}</td>`).join('')}</tr>`)
        .join('');
      html.push(`<div class="md-table-wrap"><table class="md-table">${thead}<tbody>${tbody}</tbody></table></div>`);
      continue;
    }

    // Paragraph -- gather consecutive plain lines, soft-break on newline
    closeList();
    const para: string[] = [];
    while (i < lines.length) {
      const l = lines[i] ?? '';
      if (
        /^\s*$/.test(l) ||
        FENCE_OPEN.test(l) ||
        /^#{1,6}\s+/.test(l) ||
        /^>\s?/.test(l) ||
        parseListItem(l) !== null ||
        (looksLikeRow(l) && looksLikeRow(lines[i + 1] ?? ''))
      ) {
        break;
      }
      para.push(l);
      i++;
    }
    html.push(`<p>${renderInline(escapeHtml(para.join('\n'))).replace(/\n/g, '<br/>')}</p>`);
  }

  closeList();
  let result = html.join('');
  // Splice rendered KaTeX back where the sentinel tokens landed. Block tokens
  // sit inside their own <p>…</p> — unwrap that so the display span isn't a
  // paragraph child (keeps vertical rhythm and valid nesting).
  if (math.length) {
    result = result
      .replace(new RegExp(`<p>${MATH_SENTINEL}(\\d+)${MATH_SENTINEL}</p>`, 'g'), (_m, n: string) => math[Number(n)] ?? '')
      .replace(new RegExp(`${MATH_SENTINEL}(\\d+)${MATH_SENTINEL}`, 'g'), (_m, n: string) => math[Number(n)] ?? '');
  }
  return result;
}
